import GraphObject from './graphObject.js';
import CoreUtils from '../../utils/coreUtils.js';
import GlobalDefs from '../../globalDefs.js';
import { PARAM_TYPE } from './connector.js';
import InputConnector from './inputConnector.js';
import OutputConnector from './outputConnector.js';
import Utils from './utils.js';
import { ENABLE_FILE_NONE, ENABLE_CACHING, DEBUG_PRINT_DETAILED_COOK_INFO } from './constants.js';
import CachedResult from './cachedResult.js';

const USE_PBR_MATERIAL = true;

/**
 * A node is an operator that accepts any number of inputs of any type, performs
 * some operation on them, and outputs the result. This is a base class for all
 * specialized nodes in a graph such as transform, create a box, etc. 
 * 
 * Node's parameters are members of an {@link InputConnector} type and are also its connectors;
 * a node's outputs are members of the {@link OutputConnector} type.
 * 
 * Nodes are always contained in a {@link Subnet}. Please note that nodes in general may
 * operate on any kind of data, and are note limited to 3D geometry per se.
 * 
 */

let ASSET_CHOICES = {};
export default class Node extends GraphObject
{
	constructor(pParent)
	{
		super(pParent);

		this.inputs = [];
		this.outputs = [];

		// Groups for organizing inputs into categories
		this.inputGroups = [];

		this.isBypassed = false;

		this.lastOutputResults = null;
		this.lastOutputCookIndex = -1;

		this.persistentOutputResults = {};

		this.presets = [];

		this.isDirty = true;

		this.lastError = '';
		this.lastWarning = '';

		this.takeInputResultsFromParent = false;
	}

	getLastError()
	{
		return this.lastError;
	}

	getLastWarning()
	{
		return this.lastWarning;
	}

	addWarning(strWarning)
	{
		if(this.lastWarning.length > 0)
			this.lastWarning += '; ';
		this.lastWarning += strWarning;
	}

	dispose()
	{
		super.dispose();
		this.clearPersistentCache();
	}

	getAllowResultCaching()
	{
		if(this.getAllowResultCachingSubclass())
			return true;

		return false;
	}

	getAllowResultCachingSubclass()
	{
		return false;
	}

	clearPersistentCache()
	{
		for(let strOutputName in this.persistentOutputResults)
		{
			if(this.persistentOutputResults[strOutputName])
				this.persistentOutputResults[strOutputName].dispose();
		}
		this.persistentOutputResults = {};
		if(DEBUG_PRINT_DETAILED_COOK_INFO)
			CoreUtils.log(`Cleared persistentOutputResults for ${this.getId()}`);
	}

	markDirtyInternal(iCookCount)
	{
		if(DEBUG_PRINT_DETAILED_COOK_INFO)
			CoreUtils.log(`Mark dirty internal ${this.getId()}`);
		super.markDirtyInternal(iCookCount);
		this.clearPersistentCache();
	}

	markDirty()
	{
		if(DEBUG_PRINT_DETAILED_COOK_INFO)
			CoreUtils.log(`Mark dirty ${this.getId()}`);
		super.markDirty();
		this.clearPersistentCache();
	}

	getIsBypassed()
	{
		return this.isBypassed;
	}

	setIsBypassed(bValue)
	{
		this.isBypassed = bValue;
		this.markDirty();
	}

	createGroup(strGroupName)
	{
		while(true)
		{
			if(this.getGroup(strGroupName))
			{
				// Modify
				strGroupName = CoreUtils.incrementNumericSuffix(strGroupName);
			}
			else
				break;
		}

		let pResGroup = { name: strGroupName, inputNames: []};
		this.inputGroups.push(pResGroup);
		return pResGroup;
	}

	moveGroupUp(strGroupName)
	{
		let iGroupIndex = this.getGroupIndex(strGroupName);
		if(iGroupIndex > 0)
		{
			let tempGroup = this.inputGroups[iGroupIndex - 1];
			this.inputGroups[iGroupIndex - 1] = this.inputGroups[iGroupIndex];
			this.inputGroups[iGroupIndex] = tempGroup;
		}
	}

	moveGroupDown(strGroupName)
	{
		let iGroupIndex = this.getGroupIndex(strGroupName);
		if(iGroupIndex < this.inputGroups.length - 1)
		{
			let tempGroup = this.inputGroups[iGroupIndex + 1];
			this.inputGroups[iGroupIndex + 1] = this.inputGroups[iGroupIndex];
			this.inputGroups[iGroupIndex] = tempGroup;
		}
	}

	getInputsGroup(strGroupName, bCreateIfNotFound = true)
	{
		for(let pGroup of this.inputGroups)
		{
			if(pGroup.name === strGroupName)
				return pGroup;

		}

		// If we got here, we didn't find one - create it.
		let pResGroup = null;
		if(bCreateIfNotFound)
			pResGroup = this.createGroup(strGroupName);

		return pResGroup;
	}

	setGroupName(iGroupIndex, strName)
	{
		if(iGroupIndex < this.inputGroups.length && strName.length > 0)
			this.inputGroups[iGroupIndex].name = strName;
	}

	getGroup(strGroupName)
	{
		for(let pGroup of this.inputGroups)
		{
			if(pGroup.name === strGroupName)
				return pGroup;
		}

		return null;
	}

	getGroupIndex(strGroupName)
	{
		for(let iGroup = 0; iGroup < this.inputGroups.length; iGroup++)
		{
			if(this.inputGroups[iGroup].name === strGroupName)
				return iGroup;
		}

		return -1;
	}

	getIsGroupInternal(strGroupName)
	{
		let pGroup = this.getGroup(strGroupName);
		if(!pGroup)
			return false;

		for(let strInputName of pGroup.inputNames)
		{
			let pInput = this.getInput(strInputName);
			if(pInput && !pInput.getIsInternal())
				return false;
		}

		return true;
	}

	getGroupForInputName(strInputName)
	{
		for(let pGroup of this.inputGroups)
		{
			if(pGroup.inputNames.indexOf(strInputName) >= 0)
				return pGroup;
		}

		return null;
	}

	getGroupedInputNames(bIncludeUngroupedInputs = true)
	{
		if(!bIncludeUngroupedInputs)
			return this.inputGroups;

		// Returns the same format list of group infos
		// as stored in this node, but adds inputs that are not
		// part of any group as the first group with an empty string name
		let pUngroupedInputs = {name: '', inputNames: []};
		for(let pInput of this.inputs)
		{
			const strName = pInput.getName();
			if(!this.getGroupForInputName(strName))
				pUngroupedInputs.inputNames.push(strName);
		}

		return [pUngroupedInputs, ...this.inputGroups];
	}

	addInputsToGroup(strGroupName, inputsList)
	{
		let pGroup = this.getInputsGroup(strGroupName);
		pGroup.inputNames = pGroup.inputNames.concat(inputsList);
	}

	removeInputFromGroup(strInputName)
	{
		let pGroup = this.getGroupForInputName(strInputName);
		if(!pGroup)
			return;

		let iIndexInGroup = pGroup.inputNames.indexOf(strInputName);
		if(iIndexInGroup < 0)
			return;

		pGroup.inputNames.splice(iIndexInGroup, 1);
	}

	getInputs(bConnectedOnly = false, bNonHiddenOnly = false)
	{
		let pRes = this.inputs;
		if(bConnectedOnly)
			pRes = this.inputs.filter((pInput) => { return pInput.getConnectedNode(); });

		if(bNonHiddenOnly)
			pRes = pRes.filter((pInput) => { return !pInput.getIsWholeNodeInputHidden(); });

		return pRes;
	}

	getOutputs(bGetEffectiveOutputs = false)
	{
		return this.outputs;
	}

	setTakeInputResultsFromParent(bValue)
	{
		this.takeInputResultsFromParent = bValue;
	}

	getDoAllowUserOutputFor(inputType)
	{
		return true;
	}

	ensureInputNameSafe(strNameIn, bCheckInputsOnly = false)
	{
		let strName = CoreUtils.replaceAll(strNameIn, '.', '_').trim();
		while(true)
		{
			if(this.getInput(strName) || (!bCheckInputsOnly && this.getOutput(strName)))
			{
				// Modify
				strName = CoreUtils.incrementNumericSuffix(strName);
			}
			else
				break;
		}

		strName = CoreUtils.replaceAll(strName, '\\', '_');
		return strName;
	}

	addUserInput(strName, optType = PARAM_TYPE.ANY, parameterConfig = {}, bIsLoading = false, bAddOutput = true, bCheckInputsOnly = false)
	{
		// Ensure the name is unique
		strName = this.ensureInputNameSafe(strName, bCheckInputsOnly);

		let pNewInput = this.addInput(optType, strName, parameterConfig.initialValue, parameterConfig);
		pNewInput.setIsUserConnector(true);
		if(bAddOutput && this.getDoAllowUserOutputFor(optType))
		{
			let pNewOutput = this.addOutput(optType, strName);
			pNewOutput.setIsUserConnector(true);
		}
		return pNewInput;
	}

	addInput(eType, strName, initialValue, parameterConfig = {})
	{
		if(!strName || strName.length === 0)
			strName = 'input' + (this.inputs.length + 1);

		let pInput = new InputConnector(eType, strName, this, initialValue, parameterConfig);
		this.inputs.push(pInput);
		return pInput;
	}

	renameInput(strOldName, strNewName)
	{
		strNewName = this.ensureInputNameSafe(strNewName);

		// This is tricky, because we must rename the matching output, if any,
		// and must also manage connections that also have the name stored.
		let pInput = this.getInput(strOldName);
		let pOtherNode = pInput.getConnectedNode();
		if(pOtherNode)
		{
			let pOtherOutput = pOtherNode.getOutput(pInput.getConnectedOutputName());
			pOtherOutput.renameConnectedInputInfoUnsafe(this, strOldName, strNewName);
		}

		pInput.renameUnsafe(strNewName)

		let pGroup = this.getGroupForInputName(strOldName);
		if(pGroup)
		{
			let iIndexInGroup = pGroup.inputNames.indexOf(strOldName);
			pGroup.inputNames[iIndexInGroup] = strNewName;
		}

		let pMatchingOutput = this.getOutput(strOldName);
		if(pMatchingOutput)
		{
			// We now must update both the output and any inputs it is connected to:
			pMatchingOutput.renameUnsafe(strNewName);
			pMatchingOutput.renameInputConnectedNamesUnsafe(strNewName);
		}
	}

	deleteInput(strInputName)
	{
		for(let iInput = this.inputs.length - 1; iInput >= 0; iInput--)
		{
			let pInput = this.inputs[iInput];
			if(pInput.getName() !== strInputName)
				continue;

			pInput.disconnect();
			this.removeInputFromGroup(strInputName);

			this.inputs.splice(iInput, 1);
		}
	}

	deleteOutput(strOutputName)
	{
		for(let iOutput = this.outputs.length - 1; iOutput >= 0; iOutput--)
		{
			let pOutput = this.outputs[iOutput];
			if(pOutput.getName() !== strOutputName)
				continue;

			pOutput.disconnect();
			this.outputs.splice(iOutput, 1);
		}
	}

	deleteGroup(strGroupName)
	{
		for(let iGroup = this.inputGroups.length - 1; iGroup >= 0; iGroup--)
		{
			let pGroup = this.inputGroups[iGroup];
			if(pGroup.name !== strGroupName)
				continue;

			this.inputGroups.splice(iGroup, 1);
		}
	}

	getInputIndex(strName)
	{
		for(let iInput = this.inputs.length - 1; iInput >= 0; iInput--)
		{
			let pInput = this.inputs[iInput];
			if(pInput.getName() === strName)
				return iInput;
		}

		return -1;
	}

	getInputForSpecKey(specKey)
	{
		const lastKeyElement = specKey[specKey.length - 1];
		for(let iInput = 0; iInput < this.inputs.length; iInput++)
		{
			let pInput = this.inputs[iInput];
			const inputSpecKey = pInput.getSpecId();
			if(inputSpecKey === lastKeyElement)
				return pInput;

			// Otherwise, try to split it into a list 
			if(inputSpecKey)
			{
				let fullKey = CoreUtils.replaceAll(inputSpecKey, ',', ' ').split(' ').filter(elem => elem.length > 0);
				if(CoreUtils.areArraysEqual(fullKey, specKey))
					return pInput;
			}
		}

		return null;
	}

	getInputForType(eType)
	{
		for(let iInput = 0; iInput < this.inputs.length; iInput++)
		{
			let pInput = this.inputs[iInput];
			if(pInput.getType() === eType)
				return pInput;
		}

		return this.inputs[0];
	}

	rearrangeInput(strInputName, strTargetInputOrGroupName, bDoPutAfter)
	{
		let iExistingIndex = this.getInputIndex(strInputName);
		let pInput = this.inputs[iExistingIndex];
		this.inputs.splice(iExistingIndex, 1);

		let iNewIndex = this.getInputIndex(strTargetInputOrGroupName);
		if(iNewIndex >= 0)
		{
			if(bDoPutAfter)
				iNewIndex++;
			this.inputs.splice(iNewIndex, 0, pInput);
		}
		else
			this.inputs.push(pInput);

		// Take care of group ownership
		let pOldGroup = this.getGroupForInputName(strInputName);
		if(pOldGroup)
		{
			let iIndex = pOldGroup.inputNames.indexOf(strInputName);
			pOldGroup.inputNames.splice(iIndex, 1);
		}

		let pNewGroup = this.getGroupForInputName(strTargetInputOrGroupName);
		if(!pNewGroup)
			pNewGroup = this.getGroup(strTargetInputOrGroupName);
		if(pNewGroup)
		{
			let iIndex = pNewGroup.inputNames.indexOf(strTargetInputOrGroupName);
			if(iIndex >= 0)
			{
				if(bDoPutAfter)
					iIndex++;
				pNewGroup.inputNames.splice(iIndex, 0, strInputName);
			}
			else
				pNewGroup.inputNames.push(strInputName);
		}
	}
	
	/**
	 * Sets the input of this node with the given name to a fixed, static value.
	 * @param {string} strName - the name of the input to set
	 * @param {any} value - the value to set it to
	 */
	setInputValue(strName, value)
	{
		let didChange = false;
		let strNameComponents = strName.split('.');
		let pInput = this.getInput(strNameComponents[0]);
		if(pInput)
			didChange = pInput.setStaticValue(value, strNameComponents[1]);
		else {
			console.warn(`setInputValue: no input ${strName} found on ${this.getLabel()}`);
		}
		return didChange;
	}

	/**
	 * Connects the input of this node to the output of another node.
	 * @param {string} strName - the name of the input to set
	 * @param {Node} pOtherNode - the node object to connect to this input
	 * @param {string} strOptOutput - an optional output name on that node; if not specified,
	 * the first output on that node will be used.
	 */
	setInputNode(strName, pOtherNode, strOptOutput)
	{
		let pInput = this.getInput(strName);
	
		if(!strOptOutput)
		{
			let pOtherOutputs = pOtherNode.getOutputs();
			strOptOutput = pOtherOutputs[pOtherOutputs.length - 1].getName();
		}	

		pInput.disconnect();
		pInput.connectTo(pOtherNode.getId(), strOptOutput);

		this.markDirty();
	}
	
	getInput(strName)
	{
		let strNameComponents = strName.split('.');
		for(let pInput of this.inputs)
		{
			if(pInput.getName() === strNameComponents[0])
				return pInput;
		}
		
		return null;
	}

	getOutput(strName)
	{
		for(let pOutput of this.outputs)
		{
			if(pOutput.getName() === strName)
				return pOutput;
		}
		
		return null;
	}

	getOutputConnectedTo(idOtherNode, strInputName)
	{
		for(let pOutput of this.outputs)
		{
			if(pOutput.isConnectedTo(idOtherNode, strInputName))
				return pOutput;
		}	
		return null;
	}

	getInputNode(strInputName)
	{
		let pInput = this.getInput(strInputName);
		if(pInput)
			return pInput.getConnectedNode();
		return null;
	}

	getInputNodeOutputName(strInputName)
	{
		let pInput = this.getInput(strInputName);
		if(pInput)
			return pInput.getConnectedOutputName();
		return null;
	}

	addOutput(eType, strName)
	{
		let pOutput = new OutputConnector(eType, strName, this);
		this.outputs.push(pOutput);
		return pOutput;
	}

	getType()
	{
		// Overridden
		return undefined;
	}

	getGeneratedMeshName()
	{
		return this.getType() + this.getId();
	}

	getGeneratedElemName()
	{
		return 'n' + this.getId();
	}

	getInputNames()
	{
		let pInputNames = [];
		this.inputs.forEach((pInput) => { pInputNames.push(pInput.getName()); });
		return pInputNames;
	}
	
	getHaveValidCachedOutputResults()
	{
		const iCookCount = this.getGraph().getCookCount();
		if(iCookCount === this.lastOutputCookIndex && !this.isDirty)
			return true;

		return false;
	}

	generate(scene, optStrOutputName)
	{
		if(!this.getHaveValidCachedOutputResults())
		{			
			this.markClean();
			this.lastError = '';
			this.lastWarning = '';
			this.lastOutputResults = null;
			this.lastOutputCookIndex = this.getGraph().getCookCount();

			// See if we have the computed results for everything:
			let pOutputs = this.getOutputs(true);
			let bNeedToRecomputeInputs = false;
			for(let pOutput of pOutputs)
			{
				if(!this.persistentOutputResults[pOutput.getName()])
				{
					bNeedToRecomputeInputs = true;
					break;
				}
			}

			let computedInputResults = {};
			if(bNeedToRecomputeInputs)
			{
				let pInputNames = this.getInputNames();

				// Go through all of our inputs, ask them to generate
				// We first compute results that don't use an expression
				for(let pInput of this.inputs)
				{
					if(!pInput.getUseExpression())
						computedInputResults[pInput.getName()] = this.computeInputResult(pInput, scene, pInputNames, computedInputResults);
				}
				// Now compute ones with expression since they may be dependent on the above
				for(let pInput of this.inputs)
				{
					if(pInput.getUseExpression())
						computedInputResults[pInput.getName()] = this.computeInputResult(pInput, scene, pInputNames, computedInputResults);
				}
			}
			// Now, ask our subclass to generate
			this.lastOutputResults = {};
			for(let pOutput of pOutputs)
				this.lastOutputResults[pOutput.getName()] = this.generateResultForOutput(pOutput, scene, computedInputResults, this.lastOutputResults);
		}

		if(DEBUG_PRINT_DETAILED_COOK_INFO)
		{
			console.log(`Cooked node ${this.getLabel()} id ${this.getId()}`);
			console.log(`-------------------------------------------------`);
			let pOutputNames = this.getOutputs(true);
			for(let pOutput of pOutputNames)
			{
				let pResult = this.lastOutputResults[pOutput.getName()];
				if(!pResult || !Utils.getIsMeshObject(pResult[0]))
					pResult = JSON.stringify(pResult);
				console.log(`Node ${this.getLabel()} Output ${pOutput.getName()} RESULT = ${pResult}`);
			}
		}

		if(optStrOutputName)
			return this.lastOutputResults[optStrOutputName];
		else
			return this.lastOutputResults;
	}

	/*
	// This is an attempt to automatically duplicate meshes on outputs that have more than
	// one connection going out. It works - however, it seriously slows down graph generration.
	// This is why it's disabled for now.
	ensureUniqueMeshesIfNecessary(outputResult, strOutputName)
	{
		const pOutput = this.getOutput(strOutputName);
		const bMakeMeshesUnique = pOutput.getConnectedNodeInfos().length > 1;
		if(!bMakeMeshesUnique || !Array.isArray(outputResult))
			return outputResult;

		let res = [];
		for(let pItem of outputResult)
		{
			let newItem = pItem;
			if(Utils.getIsMeshObject(pItem))
				newItem = Utils3D.cloneMesh(pItem, pItem.name);
			res.push(newItem);
		}

		return res;
	}
	*/

	computeInputResult(pInput, scene, pInputNames, computedInputResults)
	{
		if(this.takeInputResultsFromParent)
		{
			let pParentSubnet = this.getParent();
			let pSubnetInput = pParentSubnet.getInput(pInput.getName());

			// We may not have a parent subnet input for inputs that are
			// marked as internal and are not exposed.
			if(pSubnetInput)
			{
				let pSubnetInputValue = pParentSubnet.computeInputResult(pSubnetInput, scene, pParentSubnet.getInputNames(), computedInputResults);

				// Exception - we have cases where there is a node connected on the inside of the subnet for things such as curves.
				// We don't do meshes because those get stuck as shown now.
				if(!(pInput.getConnectedNode() && pInput.getType() === PARAM_TYPE.SPLINE))
				{
					pInput.storeLastComputedValueInternal(pSubnetInputValue);
					return pSubnetInputValue;
				}
			}
		}

		let pRes = null;
		try
		{
			pRes = pInput.computeCurrentValue(scene, pInputNames, computedInputResults);
		}
		catch(e)
		{
			this.lastError = e.message;
		}

		return pRes;
	}

	generateResultForOutput(pOutput, scene, computedInputResults, computedOutputResults)
	{
		if(!pOutput)
			return null;

		const strOutputName = pOutput.getName();

		if(this.persistentOutputResults[strOutputName])
		{
			let cachedValue = this.persistentOutputResults[strOutputName].toValue(scene);
			// We deliberately only call the graph-level mesh created, since the node-level
			// one is assigning parent attributes, etc., which we don't want, since we do
			// preserve them within the cached value. Graph-level callback is concerned with
			// external functionality, such as including this mesh in shadow casting list, etc.
			if(cachedValue && cachedValue.length && this.getGraph().onMeshCreated)
			{
				let pGraph = this.getGraph();
				cachedValue.forEach((pObject) => {
					if(Utils.getIsMeshObject(pObject))
						pGraph.onMeshCreated(pObject, this);
				});
			}
			return cachedValue;
		}

		if(this.canUseMatchingInputForOutputResult(pOutput))
			return computedInputResults[strOutputName];

		let pResult = null;
		try 
		{
			pResult = this.generateSubclass(scene, computedInputResults, strOutputName, computedOutputResults);
			if(ENABLE_CACHING && this.getAllowResultCaching())
			{
				if(pResult && Array.isArray(pResult) && Utils.getIsMeshObject(pResult[0]))
				{
					// Disable meshes:
					for(let pMesh of pResult)
						pMesh.setEnabled(false);
				}

				if(this.persistentOutputResults[strOutputName])
					this.persistentOutputResults[strOutputName].dispose();
				this.persistentOutputResults[strOutputName] = new CachedResult(pResult);
				if(DEBUG_PRINT_DETAILED_COOK_INFO)
					CoreUtils.log(`Store persistent result for ${this.getId()} output ${strOutputName}`);
			}
		}
		catch(e)
		{
			this.lastError = e.message;
			console.error("Error generating node result for " + this.getLabel() + ": " + e.message);
		}

		this.verifyOutputValidity(pOutput);

		return pResult;
	}

	verifyOutputValidity(pOutput)
	{
		if(pOutput.getType() != PARAM_TYPE.GEOMETRY)
			return;

		let pParentSubnet = this.getParent();
		if(!pParentSubnet)
			return;

		const NON_TRANSFORMING_NODE_TYPES = ['computeBBox', 'duplicate'];

		// Check that we're not using the same mesh to multiple outputs
		// that transform it. This check is approximate.
		let numTransformingConnectedNodes = 0;
		let pNodeInfos = pOutput.getConnectedNodeInfos();
		for(let pInfo of pNodeInfos)
		{
			let pCurrOutNode = pParentSubnet.getNode(pInfo.nodeId);
			if(!pCurrOutNode)
				continue;

			// We have some nodes like Switch that can select anything, but their
			// output type is geo. Let's check if the output is valid and whether
			// it's actually a mesh.
			// Note that we deliberately *return* here, because not all output
			// nodes might have a valid computed input value. It should be the same
			// for all of them since we're asking about this one specific output. 
			let currResult = pCurrOutNode.getLastInputResult(pInfo.inputName);
			if(currResult && !Array.isArray(currResult))
				return;

			const nodeType = pCurrOutNode.getType();
			if(NON_TRANSFORMING_NODE_TYPES.includes(nodeType))
				continue;

			if(nodeType == 'align' && pInfo.inputName === 'alignTarget')
				continue;

			numTransformingConnectedNodes++;
		}

		if(numTransformingConnectedNodes > 1) {
			this.addWarning('A mesh is being output to multiple nodes that transform it; the results may be unpredictable. Please either duplicate it on every input, or restructure the graph to avoid this.');
		}
	}

	updateToLatestVersion()
	{
		const newType = this.getGraph().getNodeTypeInfo(this.getType())['type'];

		// We re-create and re-connect the node automatically because even if there is
		// no new version, we may want to recreate the existing version anyway for
		// other fixes.

		// Create the new node:
		const parentSubnet = this.getParent();
		const vecCenter = this.getCenter();
		const newNode = parentSubnet.createNode(newType, this.vecCenter.x, vecCenter.y);

		const outputInfos = [];
		for(let pOutput of this.outputs)
		{
			const pNodeInfos = pOutput.getConnectedNodeInfos();
			for(let pInfo of pNodeInfos)
			{
				let pCurrOutNode = parentSubnet.getNode(pInfo.nodeId);
				if(!pCurrOutNode)
					continue;

				outputInfos.push({
					outNode: pCurrOutNode,
					inputName: pInfo.inputName,
					outputName: pOutput.getName()
				});
			}
		}

		for(let pInput of this.inputs)
		{
			const newInput = newNode.getInput(pInput.getName());
			if(!newInput)
				continue;

			const connectedNode = pInput.getConnectedNode();
			if(connectedNode)
				newNode.setInputNode(pInput.getName(), connectedNode, pInput.getConnectedOutputName());
			else
			{
				const savedInput = pInput.saveToObject();
				newInput.loadFromObject(savedInput);
			}
		}

		// We set these separately since connecting or disconnecting nodes changes our current ones
		for(const outputInfo of outputInfos)
			outputInfo['outNode'].setInputNode(outputInfo['inputName'], newNode, outputInfo['outputName']);
		
		this.getParent().deleteNodes([this.getId()]);
	}

	canUseMatchingInputForOutputResult(pOutput)
	{
		const pMatchingInput = this.getInput(pOutput.getName());
		return pOutput.getIsUserConnector() && pMatchingInput;
	}

	getLastInputResult(strInputName)
	{
		let strNameComponents = strInputName.split('.');
		let pLastResult = this.getInput(strNameComponents[0]).getLastResult();
		if(strNameComponents.length > 1)
			pLastResult = pLastResult.getValue(strNameComponents[1]);
		return pLastResult;
	}

	getLastOutputResult(strOutputName)
	{
		if(!this.lastOutputResults)
			return null;

		return this.lastOutputResults[strOutputName];
	}

	saveToObject()
	{
		let pRes = super.saveToObject();
		
		pRes['is'] = [];
		for(let pInput of this.inputs)
			pRes['is'].push(pInput.saveToObject());

		pRes['os'] = [];
		for(let pOutput of this.outputs)
			pRes['os'].push(pOutput.saveToObject());
		
		pRes['t'] = this.getType();
		if(this.isBypassed)
			pRes['isbp'] = this.isBypassed;

		if(this.inputGroups.length > 0)
		{
			pRes['inGrs'] = [];
			for(let inputGroup of this.inputGroups)
				pRes['inGrs'].push({'n': inputGroup.name, 'is': inputGroup.inputNames.slice(0) });
		}

		if(this.presets.length > 0)
		{
			pRes['prs'] = [];
			for(let preset of this.presets)
				pRes['prs'].push({...preset});
		}

		return pRes;
	}

	static loadFromObject(pObj, pParent)
	{
		let pNode = pParent.createNodeRaw(pObj['t']);

		GraphObject.loadFromObject(pObj, pNode);

		if(pObj['isbp'])
			pNode.isBypassed = pObj['isbp'];

		// We need to call this before loading inputs
		// so that the subnet has a chance to create its inputs node.
		pNode.loadFromObjectSubclass(pObj, pParent);

		if(pObj['inGrs'])
		{
			pNode.inputGroups = [];
			for(let inputGroup of pObj['inGrs'])
				pNode.inputGroups.push({name: inputGroup['n'], inputNames: inputGroup['is']});
		}
		
		for(let pInputInfo of pObj['is'])
		{
			const strInputName = pInputInfo['n'];
			let pInput = pNode.getInput(strInputName);
			if(!pInput && pInputInfo['isUc'])
				pInput = pNode.addUserInput(strInputName, pInputInfo['t'], {}, true);

			// This is done for backwards compatibility for nodes that were saved,
			// but whose definition was changed to delete inputs. This will preserve
			// connections on this input as a result.
			if(!pInput)
			{
				console.error("Could not find input " + strInputName + " on node " + pNode.getLabel() + "; creating a dummy one.");
				pInput = pNode.addInput(PARAM_TYPE.ANY, strInputName);
			}
			pInput.loadFromObject(pInputInfo);
		}

		pNode.autoFillInputOptions();

		for(let pOutputInfo of pObj['os'])
		{
			const strOutputName = pOutputInfo['n'];
			let pOutput = pNode.getOutput(strOutputName);
			if(!pOutput)
			{
				// For procedural assets, it's normal to not have that output on load.
				if(pNode.getType() !== 'proceduralAsset')
					console.error("Could not find output " + strOutputName + " on node " + pNode.getLabel() + "; creating a dummy one.");
				pOutput = pNode.addOutput(pOutputInfo['type'], strOutputName);
			}
			pOutput.loadFromObject(pOutputInfo);
		}

		pNode.presets = [];
		if(pObj['prs'])
		{
			for(let preset of pObj['prs'])
				pNode.presets.push(preset);
		}

		// Note that this is deliberately not a markDirty() call since we don't want callbacks and recursion up the parent chain
		// when loading.
		this.isDirty = true;
		return pNode;
	}

	loadFromObjectSubclass(pObj, pParent)
	{
		// Nothing for base class
	}

	disconnectAllInputs()
	{
		for(let pInput of this.inputs)
			pInput.disconnect();
	}

	disconnectAllInputsOutputs()
	{
		for(let pInput of this.inputs)
			pInput.disconnect();

		for(let pOutput of this.outputs)
			pOutput.disconnect();
	}

	onMeshCreated(pMesh)
	{
		if(!pMesh)
			return;

		let pGraph = this.getGraph();
		pMesh.parentGraphId = pGraph.getGraphId();

		// We need to preserve the existing id, if any,
		// in cases where we clone a mesh that originally
		// came from a file node, and we need to know
		// that it's actually an imported mesh.
		if(!pMesh.parentNodeId)
			pMesh.parentNodeId = this.getId();

		if(ENABLE_CACHING)
			pMesh.setEnabled(false);

		if(pGraph.onMeshCreated)
			pGraph.onMeshCreated(pMesh, this);
	}

	onMaterialAssigned(pMesh)
	{
		if(!pMesh)
			return;

		pMesh.parentMaterialNodeId = this.getId();
	}

	onMaterialCreated(pMaterial)
	{
		let pGraph = this.getGraph();
		pMaterial.parentGraphId = pGraph.getGraphId();
		pMaterial.specularColor = new BABYLON.Color3(0,0,0);
	}

	getAssetChoices()
	{
		const pAssetProvider = this.getGraph().getAssetProvider();
		const pProcAssetChoices = [{key: GlobalDefs.g_strNoneKey, label: 'No Asset'}, ...pAssetProvider.getNodeGraphList()];
		if(pProcAssetChoices.length <= 1)
			return null;
		if(ASSET_CHOICES[PARAM_TYPE.DROPDOWN_3D_MODEL])
			return ASSET_CHOICES;

		const pAssetChoices = ENABLE_FILE_NONE ? [{key: GlobalDefs.g_strNoneKey, label: 'No Asset'}, ...pAssetProvider.get3DAssetList()] : pAssetProvider.get3DAssetList();
		const pPathChoices = pAssetProvider.getPathList();
		const pMaterialChoices = [{key: GlobalDefs.g_strNoneKey, label: 'No Material'}, ...pAssetProvider.getMaterialList()];
		const pTextureChoices = [{key: GlobalDefs.g_strNoneKey, label: 'No Texture'}, ...pAssetProvider.getTextureList()];

		ASSET_CHOICES[PARAM_TYPE.DROPDOWN_3D_MODEL] = pAssetChoices;
		ASSET_CHOICES[PARAM_TYPE.DROPDOWN_MATERIAL] = pMaterialChoices;
		ASSET_CHOICES[PARAM_TYPE.DROPDOWN_PATH] = pPathChoices;
		ASSET_CHOICES[PARAM_TYPE.DROPDOWN_PROCEDURAL] = pProcAssetChoices;
		ASSET_CHOICES[PARAM_TYPE.DROPDOWN_TEXTURE] = pTextureChoices;

		return ASSET_CHOICES;
	}

	autoFillInputOptions(optInputsList)
	{
		let assetChoices = this.getAssetChoices();
		if(!assetChoices)
			return;

		// Some parameters, such as materials, 3d models, etc.
		// have dropdown options that are determined by other sources.
		// Auto-fill them here.
		let pInputs = optInputsList;
		if(!optInputsList)
			pInputs = this.getInputs();
		for(let pInput of pInputs)
		{
			let eType = pInput.getType(); 
			let assetsForType = assetChoices[eType];
			if(assetsForType)			
				pInput.setDropdownOptions(assetsForType);
		}
	}

	getSettableInputOfType(paramType)
	{
		// Find the first input with this type, use the name code:
		let input = this.getInputForType(paramType);
		if(!input)
			return null;

		return this.getSettableInput(input.getName());
	}

	getSettableInput(strName)
	{
		// Tries to find an input on this node, but if it's connected to anything,
		// tries to trace it up the connection chain:
		let pInitialInput = this.getInput(strName);
		if(!pInitialInput)
			return null;

		let strCurrName = strName;
		let pCurrNode = this;
		do
		{
			let pConnectedNode = pCurrNode.getInputNode(strCurrName);
			if(!pConnectedNode)
			{
				// Try to see if we have a parent that overrides this input
				let pResInput;
				let pParentSubnet = this.getParent();
				if(pParentSubnet && pParentSubnet.getInput(strCurrName))
					pResInput = pParentSubnet.getSettableInput(strCurrName);
				else
					pResInput = pCurrNode.getInput(strCurrName);
				return pResInput;
			}

			strCurrName = pCurrNode.getInputNodeOutputName(strCurrName);
			pCurrNode = pConnectedNode;
		}
		while(true);
	}

	getIsInputEnabled(strInputName)
	{
		let pInput = this.getInput(strInputName);
		if(pInput.getConnectedNode())
			return false;

		if(this.getIsInputEnabledSubclass && !this.getIsInputEnabledSubclass(strInputName))
			return false;

		return true;
	}

	onInputChanged(strInputName)
	{
		this.markDirty();
		this.dirtyUpstreamContainedNodes(strInputName);
		if(this.onInputChangedSubclass)
			this.onInputChangedSubclass(strInputName);
	}

	getContainsOtherNodes()
	{
		return false;
	}

	savePresetInputsToObject()
	{
		let values = {};
		let pInputs = this.getInputs();
		for(let pInput of pInputs)
		{
			// We don't save connected inputs as part of preset
			if(pInput.getConnectedNode())
				continue;

			let eType = pInput.getType();
			if(eType == PARAM_TYPE.GEOMETRY || eType == PARAM_TYPE.BBOX)
				continue;

			if(pInput.getIsInternal())
				continue;

			values[pInput.getName()] = pInput.saveStaticValue();
		}

		return values;
	}

	saveInputsAsPreset(strName)
	{
		// Remove the old version in case there is one
		this.removePreset(strName);

		let pNewPreset = { 
			name: strName, 
			values: this.savePresetInputsToObject(),
		};

		this.presets.push(pNewPreset);
		return pNewPreset;
	}

	getPresetIndex(strName)
	{
		for(let idx in this.presets)
		{
			if(this.presets[idx]['name'] == strName)
				return idx;
		}

		return -1;
	}

	applyPreset(strName)
	{
		let pPreset = this.presets[this.getPresetIndex(strName)];
		this.applyPresetFromValues(pPreset['values']);
	}

	applyPresetFromValues(presetValues, optOnlyInputType = null)
	{
		let iNumInputsLoaded = 0;
		let pInputs = this.getInputs();
		for(let pInput of pInputs)
		{
			// We don't touch connected inputs since they're overriden anyway
			if(pInput.getConnectedNode())
				continue;

			if(pInput.getIsInternal())
				continue;

			if(optOnlyInputType != null && pInput.getType() != optOnlyInputType)
				continue;

			const parmValue = presetValues[pInput.getName()];
			if(parmValue !== undefined)
			{
				if(pInput.loadStaticValueFrom(parmValue))
					iNumInputsLoaded++;
			}
		}

		return iNumInputsLoaded;
	}

	loadInputValuesFrom(savedInputData)
	{
		savedInputData.forEach((inputData) => {
			const pInput = this.getInput(inputData['n']);
			if(!pInput || pInput.getConnectedNode())
				return;

			pInput.loadStaticValueFrom(inputData['sv']);
		});
	}

	removePreset(strName)
	{
		let iPreset = this.getPresetIndex(strName);
		if(iPreset >= 0)
			this.presets.splice(iPreset, 1);
	}

	getPresets()
	{
		return this.presets;
	}

	getVisualizedOutputName()
	{
		if(this.outputs.length === 0)
			return null;

		return this.outputs[0].getName();
	}

	dirtyUpstreamChildren()
	{
		let pNodeInfos;
		let pParentSubnet = this.getParent();
		let iCookCount = this.getGraph().getCookCount();
		for(let pOutput of this.outputs)
		{
			pNodeInfos = pOutput.getConnectedNodeInfos();
			for(let pInfo of pNodeInfos)
			{
				let pCurrOutNode = pParentSubnet.getNode(pInfo.nodeId);
				if(!pCurrOutNode)
					continue;

				// We need to dirty this before the already dirty check because
				// the node could have been dirtied from a different node previously,
				// but with a different input name, in which case, if this is a procedural node,
				// we wouldn't dirty the insides at all. We could probably try to do some
				// optimization here, but it's not trivial since it is per-input, and profiling
				// before and after this change has not shown a great performance impact.
				pCurrOutNode.dirtyUpstreamContainedNodes(pInfo.inputName);
				if(pCurrOutNode.getIsAlreadyDirtyInternal(iCookCount))
					continue;

				pCurrOutNode.markDirtyInternal(iCookCount);
				pCurrOutNode.dirtyUpstreamChildren();
			}
		}
	}

	dirtyUpstreamContainedNodes(strInputName)
	{

	}

	getRefreshEnableStatusAfterParamChange(strInputName)
	{
		return false;
	}

	updateInputsOutputs(pNewInputs, pNewOutputs = null, bHideInternalInputs = true)
	{
		let pOldInputs = this.getInputs();
		for(let iInput = pOldInputs.length - 1; iInput >= 0; iInput--)
		{
			let pOldInput = pOldInputs[iInput];
			const strInputName = pOldInput.getName();
			if(strInputName == 'assetId')
				continue;

			let foundNewInput = pNewInputs.filter((tempInput) => { return tempInput.name == strInputName; });
			if(foundNewInput.length > 0)
				continue;

			this.deleteInput(strInputName);
			this.deleteOutput(strInputName);
		}

		// Now add new inputs
		for(let iInput = 0; iInput < pNewInputs.length; iInput++)
		{
			let pNewInput = pNewInputs[iInput];
			if(bHideInternalInputs && pNewInput.getIsInternal())
				continue;

			let pConfigDict = pNewInput.getConfigDict();
			pConfigDict['isConnectorHidden'] = false;
			pConfigDict['isWholeInputHidden'] = false;

			let pInput = this.getInput(pNewInput.getName());
			if(!pInput)
			{
				pInput = this.addUserInput(pNewInput.getName(), pNewInput.getType(), pConfigDict, true, false, !!pNewOutputs);
				pInput.setStaticValue(pNewInput.getLastResult());
			}
			else
				pInput.setFromConfigDict(pConfigDict);
			pInput.setLabel(pNewInput.getLabel());
		}

		if(!pNewOutputs)
			return;

		// Now do the same for outputs:
		let pOldOutputs = this.getOutputs();
		for(let iOutput = pOldOutputs.length - 1; iOutput >= 0; iOutput--)
		{
			let pOldOutput = pOldOutputs[iOutput];
			const strOutputName = pOldOutput.getName();
			if(strOutputName == 'geo')
				continue;

			let foundNewOutput = pNewOutputs.filter((tempOutput) => { return tempOutput.name == strOutputName; });
			if(foundNewOutput.length > 0)
				continue;

			this.deleteOutput(strOutputName);
		}

		// Now add new outputs
		for(let iOutput = 0; iOutput < pNewOutputs.length; iOutput++)
		{
			let pNewOutput = pNewOutputs[iOutput];

			let pOutput = this.getOutput(pNewOutput.getName());
			if(!pOutput)
			{
				pOutput = this.addOutput(pNewOutput.getType(), pNewOutput.getName());
				pOutput.setIsUserConnector(true);
			}

			pOutput.setLabel(pNewOutput.getLabel());
		}
	}

	createAndAssignMaterial(pMesh, scene, optDiffuse = null)
	{
		if(pMesh.isDisposed())
			throw new Error(`Applying a material to a disposed mesh: ${pMesh.name} parent nework = ${this.getParent().getLabel()}`);
		if(USE_PBR_MATERIAL)
		{
			pMesh.material = new BABYLON.PBRMaterial(this.getGeneratedMeshName(), scene);
			pMesh.material.metallic = 0.0;
			pMesh.material.specularIntensity = 0.0;
			if(optDiffuse)
				pMesh.material.albedoColor = optDiffuse.clone();
		}
		else
		{
			pMesh.material = new BABYLON.StandardMaterial(this.getGeneratedMeshName(), scene);
			if(optDiffuse)
			{
				pMesh.material.diffuseColor = optDiffuse.clone();
				pMesh.material.emissiveColor = pMesh.material.diffuseColor.scale(0.25);
			}
		}
		this.onMaterialCreated(pMesh.material);
	}
}